import React, {forwardRef, ReactElement} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'
import {Badge} from '../Badge'
import {Icon} from '../Icon'
import {Text} from '../Text'

export const PILL_HEIGHT = 34

const PILL_VARIANTS = ['default', 'link'] as const

export type PillVariant = (typeof PILL_VARIANTS)[number]

type Props = {
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Pass through style to allow styles overrides */
  style?: React.CSSProperties
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Whether the pill is active or not */
  isSelected?: boolean
  /** Pill content */
  children?: React.ReactNode
  /** Action for pill click */
  onClick?: (e?: React.SyntheticEvent) => void
  /** Icon left aligned */
  iconStart?: ReactElement
  /** Icon right aligned */
  iconEnd?: ReactElement
  /** Should show down arrow icon and border */
  showArrow?: boolean
  /** Weather the button is disabled or not */
  isDisabled?: boolean
  /** If a count should be displayed */
  count?: number
  /** Pill variant */
  variant?: PillVariant
  /** If should stretch to full width of parent */
  fullWidth?: boolean
}

const createVariantStyles = (variant: string) => {
  const colors = cssTheme.colors.pill[variant as PillVariant]
  return `
    &.--variant-${variant} {
      color: ${colors.content};
      background: ${colors.background};
      border-color: ${colors.border};
      svg {
        color: ${colors.icon};
      }

      ${linariaMq.desktopXs} {
        &:hover,
        &:focus {
          background: ${colors.hover.background};
          color: ${colors.hover.content};
          border-color: ${colors.hover.border};
          svg {
            color: ${colors.hover.icon};
          }
        }

        &.--selected {
          &:hover,
          &:focus {
            background: ${colors.selected.hover.background};
            color: ${colors.selected.hover.content};
            border-color: ${colors.selected.hover.border};
            box-shadow: inset 0 0 0 1px ${colors.selected.hover.border};
            svg {
              color: ${colors.selected.hover.icon};
            }
          }
        }
      }

      &:active {
        color: ${cssTheme.colors.pill.default.active.content};
        background: ${colors.active.background};
        border-color: ${colors.active.border};
      }

      &.--selected {
        color: ${colors.selected.content};
        background: ${colors.selected.background};
        border-color: ${colors.selected.border};
        box-shadow: inset 0 0 0 1px ${colors.selected.border};
        svg {
          color: ${colors.selected.icon};
        }
        &:active {
          color: ${cssTheme.colors.pill.default.selected.active.content};
          background: ${colors.selected.active.background};
          border-color: ${colors.selected.active.border};
        }
      }
    }
  `
}

const PillElement = styled('button')`
  display: flex;
  align-items: center;
  white-space: nowrap;
  border-radius: ${cssTheme.layout.radius.rounded};
  padding: 0 ${cssTheme.layout.spacing.s400};
  outline: none;
  cursor: pointer;
  border: 1px solid;
  gap: ${cssTheme.layout.spacing.s250};
  min-height: ${PILL_HEIGHT}px;
  transition:
    background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out,
    color 0.15s ease-in-out;

  ${PILL_VARIANTS.map(createVariantStyles).join('\n')}

  &:disabled {
    background-color: ${cssTheme.colors.pill.disabled.background} !important;
    border-color: ${cssTheme.colors.pill.disabled.border} !important;
    color: ${cssTheme.colors.pill.disabled.content} !important;
    cursor: not-allowed;
    svg {
      color: ${cssTheme.colors.pill.disabled.icon} !important;
    }
  }

  &.--fullWidth {
    width: 100%;
    justify-content: center;
  }
`

export const IconWrapper = styled('span')``

export const DownArrowIconWrapper = styled('span')`
  display: flex;

  &.--variant-link {
    color: ${cssTheme.colors.pill.link.icon};
    &.--selected {
      color: ${cssTheme.colors.pill.link.selected.icon};
    }
  }
  &.--variant-default {
    color: ${cssTheme.colors.pill.default.icon};
    &.--selected {
      color: ${cssTheme.colors.pill.default.selected.icon};
    }
  }
`

export const Pill = forwardRef<HTMLButtonElement, Props>(
  (
    {
      children,
      isSelected = false,
      onClick,
      dataId,
      iconStart = null,
      iconEnd = null,
      showArrow = false,
      isDisabled = false,
      count = 0,
      variant = 'default',
      fullWidth = false,
      className,
      style
    },
    ref
  ) => {
    const handleClick = (event: React.SyntheticEvent) => {
      event.preventDefault()

      if (onClick) onClick()
    }

    return (
      <PillElement
        ref={ref}
        disabled={isDisabled}
        data-id={dataId}
        onClick={handleClick}
        className={cx(
          className,
          `--variant-${variant}`,
          isSelected && `--selected`,
          fullWidth && `--fullWidth`
        )}
        style={style}
      >
        {iconStart && <IconWrapper>{iconStart}</IconWrapper>}
        <Text variant="labelS" tag="div">
          {children}
        </Text>
        {iconEnd && <IconWrapper>{iconEnd}</IconWrapper>}
        {count > 0 && <Badge variant="info">{count}</Badge>}
        {showArrow && (
          <DownArrowIconWrapper
            className={cx(`--variant-${variant}`, isSelected && `--selected`)}
          >
            <Icon name="CaratDown" size="xs" />
          </DownArrowIconWrapper>
        )}
      </PillElement>
    )
  }
)

Pill.displayName = 'Pill'
